/**
 * Pagina configurazione reparti RT — gestione mappatura reparti registratore telematico (DEC-025).
 *
 * Consente di visualizzare, creare, modificare e disattivare
 * le mappature reparti del registratore telematico per tenant e matricola.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Plus, Settings, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

interface RepartoRT {
  id: string;
  matricola_rt: string;
  reparto_rt: number;
  descrizione: string;
  aliquota_iva: string;
  natura_iva: string | null;
  attivo: boolean;
}

const RepartiRT: React.FC = () => {
  const [reparti, setReparti] = useState<RepartoRT[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form nuovo reparto
  const [matricola, setMatricola] = useState('');
  const [reparto, setReparto] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [aliquota, setAliquota] = useState('22.00');
  const [creating, setCreating] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/cassa/reparti-rt', { headers: authHeaders() });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: RepartoRT[] = await res.json();
      setReparti(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void carica();
  }, [carica]);

  const creaReparto = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/cassa/reparti-rt', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          matricola_rt: matricola,
          reparto_rt: Number(reparto),
          descrizione,
          aliquota_iva: aliquota,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      setMatricola('');
      setReparto('');
      setDescrizione('');
      setAliquota('22.00');
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore creazione');
    } finally {
      setCreating(false);
    }
  };

  const eliminaReparto = async (id: string) => {
    try {
      const res = await fetch(`/api/v1/cassa/reparti-rt/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      await carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  return (
    <div className="space-y-6 p-6">
      <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
        Configurazione reparti RT
      </h1>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Form creazione */}
      <Card>
        <CardHeader>
          <CardTitle>Nuovo reparto RT</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">Matricola RT</label>
              <Input
                value={matricola}
                onChange={(e) => setMatricola(e.target.value)}
                placeholder="3CIBN000084"
                className="w-40"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">N. Reparto</label>
              <Input
                type="number"
                value={reparto}
                onChange={(e) => setReparto(e.target.value)}
                placeholder="1-40"
                className="w-20"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">Descrizione</label>
              <Input
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                placeholder="IVA 22%"
                className="w-48"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-muted-foreground">Aliquota IVA %</label>
              <Input
                type="number"
                step="0.01"
                value={aliquota}
                onChange={(e) => setAliquota(e.target.value)}
                className="w-24"
              />
            </div>
            <Button
              onClick={creaReparto}
              disabled={creating || !matricola || !reparto || !descrizione}
            >
              <Plus className="mr-2 h-4 w-4" />
              Aggiungi
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista reparti */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Reparti configurati
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">Caricamento…</p>
          )}
          {!loading && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Matricola</TableHead>
                  <TableHead className="text-right">Reparto</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">IVA %</TableHead>
                  <TableHead>Natura</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {reparti.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                      Nessun reparto configurato
                    </TableCell>
                  </TableRow>
                )}
                {reparti.map((r: RepartoRT) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-sm">{r.matricola_rt}</TableCell>
                    <TableCell className="text-right font-mono">{r.reparto_rt}</TableCell>
                    <TableCell>{r.descrizione}</TableCell>
                    <TableCell className="text-right font-mono">{r.aliquota_iva}</TableCell>
                    <TableCell>{r.natura_iva ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant={r.attivo ? 'outline' : 'destructive'}>
                        {r.attivo ? 'Attivo' : 'Disattivato'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => eliminaReparto(r.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RepartiRT;
